import React, { FC, useEffect, useState } from 'react'
import { Box, Button, Typography } from '@mui/material'
import { useLocation, useNavigate } from 'react-router-dom'
import { Formik } from 'formik'
import * as yup from 'yup'
import OtpInput from 'react-otp-input'
import axios from 'axios'
import {
  BaseLayout,
  ButtonContainer,
  CustomAlert,
  CustomButton,
  Loader,
  Module,
  ModuleBody,
  ModuleHeader,
} from 'components'
import { api, getAuthClient, routes } from 'config'
import { processErrorMsg } from 'utils'
import useStyles from '../mfa/mfa-style'

const maskEmail = (email: string): string => {
  if (!email || !email.includes('@')) return email
  const [local, domain] = email.split('@')
  if (local.length <= 2) return `${local[0]}***@${domain}`
  return `${local[0]}***${local.slice(-1)}@${domain}`
}

const validationSchema = yup.object().shape({
  verificationCode: yup
    .string()
    .matches(/^[0-9]{6}$/, 'Enter the 6-digit code')
    .required('Verification code is required'),
})

const ForceEmailEnroll: FC = () => {
  const { classes } = useStyles()
  const navigate = useNavigate()
  const location = useLocation()
  const locationState = location.state as { stateToken?: string } | null

  const [showLoader, setShowLoader] = useState(true)
  const [serviceError, setServiceError] = useState('')
  const [maskedEmail, setMaskedEmail] = useState('')
  const [factorId, setFactorId] = useState<string | null>(null)
  const [stateToken, setStateToken] = useState<string | null>(null)
  const [resendAlert, setResendAlert] = useState(false)
  const [disableResend, setDisableResend] = useState(true)

  useEffect(() => {
    const initialStateToken = locationState?.stateToken

    if (!initialStateToken) {
      navigate(routes.login)
      return
    }

    const oktaDomain = process.env.REACT_APP_OKTA_DOMAIN

    const initialize = async () => {
      try {
        const userInfoRes = await axios.get(api.getUserInfo(initialStateToken))
        const email = userInfoRes.data?.email
        if (!email) {
          throw new Error('Unable to look up your email. Please try again.')
        }
        setMaskedEmail(maskEmail(email))

        const enrollRes = await axios.post(
          `https://${oktaDomain}/api/v1/authn/factors`,
          {
            stateToken: initialStateToken,
            factorType: 'email',
            provider: 'OKTA',
            profile: { email },
          },
        )

        const newFactorId = enrollRes.data?._embedded?.factor?.id
        const newStateToken = enrollRes.data?.stateToken
        if (!newFactorId || !newStateToken) {
          throw new Error(
            'Unable to start email verification. Please try again.',
          )
        }
        setFactorId(newFactorId)
        setStateToken(newStateToken)
        setShowLoader(false)
        setTimeout(() => setDisableResend(false), 30000)
      } catch (err: any) {
        setShowLoader(false)
        setServiceError(
          processErrorMsg(
            err,
            'ForceEmailEnroll',
            'Initializing email enrollment',
          ),
        )
      }
    }

    initialize()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleResend = async () => {
    if (!factorId || !stateToken) return
    setShowLoader(true)
    try {
      const oktaDomain = process.env.REACT_APP_OKTA_DOMAIN
      await axios.post(
        `https://${oktaDomain}/api/v1/authn/factors/${factorId}/lifecycle/resend`,
        { stateToken },
      )
      setResendAlert(true)
      setDisableResend(true)
      setTimeout(() => setDisableResend(false), 30000)
      setTimeout(() => setResendAlert(false), 30000)
    } catch (err: any) {
      setServiceError(
        processErrorMsg(err, 'ForceEmailEnroll', 'Resending code'),
      )
    } finally {
      setShowLoader(false)
    }
  }

  const handleSubmit = async (values: { verificationCode: string }) => {
    if (!factorId || !stateToken) return
    setShowLoader(true)
    setServiceError('')
    try {
      const oktaDomain = process.env.REACT_APP_OKTA_DOMAIN
      const response = await axios.post(
        `https://${oktaDomain}/api/v1/authn/factors/${factorId}/lifecycle/activate`,
        { stateToken, passCode: values.verificationCode },
      )

      const nextStateToken = response.data?.stateToken
      if (nextStateToken) {
        const authClient = getAuthClient()
        try {
          await authClient.tx.resume({ stateToken: nextStateToken })
        } catch {
          // best-effort: enroll page also calls tx.resume()
        }
      }

      navigate(routes.enroll)
    } catch (err: any) {
      setShowLoader(false)
      setServiceError(
        processErrorMsg(err, 'ForceEmailEnroll', 'Verifying email code'),
      )
    }
  }

  return (
    <BaseLayout maxWidth="40em">
      <Loader visible={showLoader} />
      <Module serviceError={serviceError}>
        <ModuleHeader
          title="Verify Your Email"
          className={classes.otpFormHeader}
          moduleBodyClass={classes.otpModuleBodyContainer}
        >
          <p className={classes.infoContent}>
            To finish setting up two-factor authentication, enter the code we
            sent to <strong>{maskedEmail || 'your email'}</strong>. This
            one-time passcode will expire in 5 minutes.
          </p>
        </ModuleHeader>
        <ModuleBody>
          {resendAlert && (
            <CustomAlert severity="success">
              A new 6-digit code has been sent to <strong>{maskedEmail}</strong>
              . It may take up to 5 minutes for your code to arrive.
            </CustomAlert>
          )}
          <Formik
            initialValues={{ verificationCode: '' }}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({
              values: { verificationCode },
              handleSubmit: onSubmit,
              isValid,
              dirty,
              isSubmitting,
              setFieldValue,
            }) => (
              <form onSubmit={onSubmit}>
                <div className={classes.otpContainer}>
                  <Typography>
                    <strong className={classes.verificationLabel}>
                      Verification Code
                    </strong>
                  </Typography>
                  <OtpInput
                    value={verificationCode}
                    onChange={(value: string) => {
                      if (value === '' || /^[0-9]*$/.test(value)) {
                        setFieldValue('verificationCode', value)
                      }
                    }}
                    numInputs={6}
                    inputStyle="otp-input"
                    containerStyle="otp-input-container"
                    focusStyle="otp-input--focused"
                    renderInput={(props: object) => (
                      <input {...{ ...props, inputMode: 'numeric' }} />
                    )}
                    shouldAutoFocus
                  />
                </div>

                <Box textAlign="center">
                  <div className={classes.resendContainer}>
                    <Button
                      variant="text"
                      disableRipple
                      disabled={disableResend}
                      className={classes.resendInfo}
                      onClick={handleResend}
                    >
                      Didn't get the code? Resend
                    </Button>
                  </div>
                </Box>

                <ButtonContainer
                  primary={
                    <CustomButton
                      type="submit"
                      disabled={!dirty || !isValid || isSubmitting || !factorId}
                    >
                      Continue
                    </CustomButton>
                  }
                  secondary={
                    <CustomButton
                      type="button"
                      onClick={() => navigate(routes.login)}
                    >
                      Back to login
                    </CustomButton>
                  }
                />
              </form>
            )}
          </Formik>
        </ModuleBody>
      </Module>
    </BaseLayout>
  )
}

export default ForceEmailEnroll
